export type ImportEntityType =
  | 'Customer'
  | 'Supplier'
  | 'InventorySku'
  | 'Vehicle';

export type ImportStatus =
  | 'PendingAnalysis'
  | 'Analysed'
  | 'AnalysisFailed'
  | 'Approving'
  | 'Approved'
  | 'Discarded';

export type ImportRowData = Record<string, unknown> & {
  id?: string | null;
  customerId?: string | null;
  supplierId?: string | null;
  name?: string | null;
  type?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactNumber?: string | null;
  address?: string | null;
  postcode?: string | null;
  country?: string | null;
};

export interface ApiRowIssue {
  severity: string | null;
  field: string | null;
  message: string | null;
}

export interface ApiRawRow {
  sourceRow: number;
  cells: string[];
}

export interface ApiTemplateRow {
  sourceRow: number;
  customer: ImportRowData | null;
  supplier: ImportRowData | null;
  inventorySku?: ImportRowData | null;
  vehicle?: ImportRowData | null;
  parseErrors: string[] | null;
}

export interface ApiSuggestedRow {
  sourceRow: number;
  customer: ImportRowData | null;
  supplier: ImportRowData | null;
  inventorySku?: ImportRowData | null;
  vehicle?: ImportRowData | null;
  issues: ApiRowIssue[] | null;
}

export interface ApiMasterDataImport {
  id: string;
  entityType: ImportEntityType;
  fileName: string;
  status: ImportStatus;
  rowCount: number;
  templateRecognised: boolean;
  createdAt: string;
  analysedAt: string | null;
  approvedAt: string | null;
  discardedAt: string | null;
  numberOfRecordsCreated: number | null;
  numberOfExistingSkusSkipped?: number | null;
  numberOfExistingSkusUpdatedWithImages?: number | null;
  numberOfImagesImported?: number | null;
  analysisError: string | null;
  raw: { rows: ApiRawRow[] } | null;
  templateRows: ApiTemplateRow[] | null;
  analysis: {
    summary: string | null;
    submittedAt: string;
    rows: ApiSuggestedRow[] | null;
  } | null;
}

export interface ApiImportListItem {
  id: string;
  entityType: ImportEntityType;
  fileName: string;
  status: ImportStatus;
  rowCount: number;
  createdAt: string;
}

export interface ReviewRow {
  key: string;
  sourceRow: number;
  original: ImportRowData | null;
  suggested: ImportRowData | null;
  data: ImportRowData;
  issues: ApiRowIssue[];
  parseErrors: string[];
  excluded: boolean;
}

export interface RowBlocker {
  field: string | null;
  message: string;
}

export interface ApproveRowError {
  sourceRow: number;
  field: string | null;
  message: string;
}

export interface MappedApproveErrors {
  rowErrors: ApproveRowError[];
  generalErrors: string[];
}

export interface RowErrorState {
  fieldMessages: Map<string, string[]>;
  rowMessages: string[];
}

export interface EditableFieldConfig {
  key: string;
  label: string;
  getValue: (data: ImportRowData) => unknown;
  setValue: (data: ImportRowData, value: unknown) => ImportRowData;
}

interface EntityPayloadCarrier {
  customer: ImportRowData | null;
  supplier: ImportRowData | null;
  inventorySku?: ImportRowData | null;
  vehicle?: ImportRowData | null;
}

export interface EntityImportConfig {
  payloadOf: (row: EntityPayloadCarrier | null) => ImportRowData | null;
  rowKeyOf: (
    sourceRow: number,
    data: ImportRowData | null,
    ordinalWithinSourceRow: number
  ) => string;
  approveBodyKey: string;
  fluentPrefix: string;
  masterRoute: string;
  nouns: { singular: string; plural: string };
  editableFields: EditableFieldConfig[];
  rowBlockers: (row: ReviewRow) => RowBlocker[];
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const emptyToNull = (value: unknown): unknown =>
  typeof value === 'string' && value.trim() === '' ? null : value;

const trimmedString = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const pascalToCamel = (value: string): string =>
  value.length === 0 ? value : value[0].toLowerCase() + value.slice(1);

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const flatField = (key: string, label: string): EditableFieldConfig => ({
  key,
  label,
  getValue: (data) => data[key],
  setValue: (data, value) => ({ ...data, [key]: value }),
});

const nestedField = (
  key: string,
  label: string,
  parentKey: string,
  childKey: string
): EditableFieldConfig => ({
  key,
  label,
  getValue: (data) => asRecord(data[parentKey])?.[childKey],
  setValue: (data, value) => ({
    ...data,
    [parentKey]: { ...(asRecord(data[parentKey]) ?? {}), [childKey]: value },
  }),
});

const sourceRowKey = (sourceRow: number): string => String(sourceRow);

const skuRowKey = (
  sourceRow: number,
  data: ImportRowData | null,
  ordinalWithinSourceRow: number
): string => {
  const skuId = data ? trimmedString(data.skuId).toLowerCase() : '';
  const discriminator = skuId === '' ? `#${ordinalWithinSourceRow}` : skuId;
  return `${sourceRow}:${discriminator}`;
};

const nameBlocker = (value: unknown): RowBlocker | null => {
  const name = trimmedString(value);
  if (name === '') return { field: 'name', message: 'Name is required.' };
  if (name.length < 3 || name.length > 100) {
    return {
      field: 'name',
      message: 'Name must be between 3 and 100 characters.',
    };
  }
  return null;
};

const requiredNameBlocker = (value: unknown): RowBlocker | null =>
  trimmedString(value) === ''
    ? { field: 'name', message: 'Name is required.' }
    : null;

const contactEmailBlocker = (value: unknown): RowBlocker | null => {
  const email = trimmedString(value);
  if (email === '') {
    return { field: 'contactEmail', message: 'Contact email is required.' };
  }
  if (!emailPattern.test(email)) {
    return {
      field: 'contactEmail',
      message: 'Contact email must be a valid email address.',
    };
  }
  return null;
};

const addressBlocker = (value: unknown): RowBlocker | null => {
  const address = trimmedString(value);
  if (address === '')
    return { field: 'address', message: 'Address is required.' };
  if (address.length < 3 || address.length > 200) {
    return {
      field: 'address',
      message: 'Address must be between 3 and 200 characters.',
    };
  }
  return null;
};

const supplierLocationBlocker = (data: ImportRowData): RowBlocker | null => {
  const locations = Array.isArray(data.locations)
    ? (data.locations as Record<string, unknown>[])
    : [];
  const hasValidLocationAddress = locations.some((location) => {
    const address = trimmedString(location?.address);
    return address.length >= 3 && address.length <= 200;
  });
  if (hasValidLocationAddress) return null;
  return {
    field: null,
    message:
      'At least one location with a valid address (3–200 characters) is required. Fix the file or exclude this row.',
  };
};

const customerBlockers = (data: ImportRowData): RowBlocker[] => {
  const blockers: RowBlocker[] = [];
  const name = nameBlocker(data.name);
  if (name) blockers.push(name);
  const email = contactEmailBlocker(data.contactEmail);
  if (email) blockers.push(email);
  const address = addressBlocker(data.address);
  if (address) blockers.push(address);
  return blockers;
};

const supplierBlockers = (data: ImportRowData): RowBlocker[] => {
  const blockers: RowBlocker[] = [];
  const name = nameBlocker(data.name);
  if (name) blockers.push(name);
  const email = contactEmailBlocker(data.contactEmail);
  if (email) blockers.push(email);
  const location = supplierLocationBlocker(data);
  if (location) blockers.push(location);
  return blockers;
};

const inventorySkuBlockers = (data: ImportRowData): RowBlocker[] => {
  const blockers: RowBlocker[] = [];

  const skuId = trimmedString(data.skuId);
  if (skuId === '') {
    blockers.push({ field: 'skuId', message: 'SKU ID is required.' });
  } else if (skuId.length < 3 || skuId.length > 25) {
    blockers.push({
      field: 'skuId',
      message: 'SKU ID must be between 3 and 25 characters.',
    });
  }

  const name = requiredNameBlocker(data.name);
  if (name) blockers.push(name);

  if (trimmedString(data.unitOfMeasure) === '') {
    blockers.push({
      field: 'unitOfMeasure',
      message: 'Unit of measure is required.',
    });
  }

  const amount = toNumber(data.amountOfProductInSku);
  if (amount === null || amount < 1) {
    blockers.push({
      field: 'amountOfProductInSku',
      message: 'Products per SKU must be 1 or greater.',
    });
  }

  const dimensions = asRecord(data.dimensions);
  const length = dimensions ? toNumber(dimensions.length) : null;
  const width = dimensions ? toNumber(dimensions.width) : null;
  const height = dimensions ? toNumber(dimensions.height) : null;
  const dimensionsUnit = dimensions
    ? trimmedString(dimensions.dimensionsUnitOfMeasure)
    : '';
  if (
    !dimensions ||
    length === null ||
    length <= 0 ||
    width === null ||
    width <= 0 ||
    height === null ||
    height <= 0 ||
    dimensionsUnit === ''
  ) {
    blockers.push({
      field: null,
      message:
        'Dimensions (length, width, height and unit) are required and must be greater than 0.',
    });
  }

  const grossWeight = toNumber(data.grossWeight);
  if (grossWeight === null || grossWeight <= 0) {
    blockers.push({
      field: 'grossWeight',
      message: 'Gross weight must be greater than 0.',
    });
  }
  if (trimmedString(data.grossWeightUnitOfMeasure) === '') {
    blockers.push({
      field: null,
      message: 'Gross weight unit of measure is required.',
    });
  }

  const palletising = Array.isArray(data.palletising) ? data.palletising : [];
  if (palletising.length === 0) {
    blockers.push({
      field: null,
      message: 'At least one palletising configuration is required.',
    });
  }

  if (trimmedString(asRecord(data.productDetails)?.productId) === '') {
    blockers.push({
      field: 'productDetails.productId',
      message: 'A product must be assigned to this SKU.',
    });
  }

  return blockers;
};

const vehicleBlockers = (data: ImportRowData): RowBlocker[] => {
  const blockers: RowBlocker[] = [];

  if (trimmedString(data.model) === '') {
    blockers.push({ field: 'model', message: 'Model is required.' });
  }

  if (trimmedString(data.registrationNumber) === '') {
    blockers.push({
      field: 'registrationNumber',
      message: 'Registration number is required.',
    });
  }

  if (trimmedString(data.vehicleType) === '') {
    blockers.push({
      field: 'vehicleType',
      message: 'Vehicle type is required.',
    });
  }

  if (trimmedString(data.vehicleCategory) === '') {
    blockers.push({
      field: 'vehicleCategory',
      message: 'Vehicle category is required.',
    });
  }

  if (trimmedString(data.fuelType) === '') {
    blockers.push({ field: 'fuelType', message: 'Fuel type is required.' });
  }

  if (trimmedString(data.averageAnnualMileageUnits) === '') {
    blockers.push({
      field: null,
      message: 'Average annual mileage units (miles or km) are required.',
    });
  }

  const capacity = asRecord(data.vehicleCapacity);
  const dimensions = capacity ? asRecord(capacity.maxCapacityDimensions) : null;
  const length = dimensions ? toNumber(dimensions.length) : null;
  const width = dimensions ? toNumber(dimensions.width) : null;
  const height = dimensions ? toNumber(dimensions.height) : null;
  if (
    !dimensions ||
    length === null ||
    length <= 0 ||
    width === null ||
    width <= 0 ||
    height === null ||
    height <= 0
  ) {
    blockers.push({
      field: null,
      message:
        'Vehicle capacity dimensions (length, width and height) are required and must be greater than 0.',
    });
  }

  return blockers;
};

const skipWhenExcluded =
  (build: (data: ImportRowData) => RowBlocker[]) =>
  (row: ReviewRow): RowBlocker[] =>
    row.excluded ? [] : build(row.data);

export const entityImportConfig: Record<ImportEntityType, EntityImportConfig> =
  {
    Customer: {
      payloadOf: (row) => row?.customer ?? null,
      rowKeyOf: (sourceRow) => sourceRowKey(sourceRow),
      approveBodyKey: 'customerRows',
      fluentPrefix: 'CustomerRows',
      masterRoute: '/customer-master',
      nouns: { singular: 'customer', plural: 'customers' },
      editableFields: [
        flatField('customerId', 'Customer ID'),
        flatField('name', 'Name'),
        flatField('contactName', 'Contact name'),
        flatField('contactEmail', 'Contact email'),
        flatField('address', 'Address'),
        flatField('postcode', 'Postcode'),
      ],
      rowBlockers: skipWhenExcluded(customerBlockers),
    },
    Supplier: {
      payloadOf: (row) => row?.supplier ?? null,
      rowKeyOf: (sourceRow) => sourceRowKey(sourceRow),
      approveBodyKey: 'supplierRows',
      fluentPrefix: 'SupplierRows',
      masterRoute: '/supplier-master',
      nouns: { singular: 'supplier', plural: 'suppliers' },
      editableFields: [
        flatField('supplierId', 'Supplier ID'),
        flatField('name', 'Name'),
        flatField('contactName', 'Contact name'),
        flatField('contactEmail', 'Contact email'),
      ],
      rowBlockers: skipWhenExcluded(supplierBlockers),
    },
    InventorySku: {
      payloadOf: (row) => row?.inventorySku ?? null,
      rowKeyOf: skuRowKey,
      approveBodyKey: 'inventorySkuRows',
      fluentPrefix: 'InventorySkuRows',
      masterRoute: '/sku-master',
      nouns: { singular: 'SKU', plural: 'SKUs' },
      editableFields: [
        flatField('skuId', 'SKU ID'),
        flatField('name', 'Name'),
        flatField('unitOfMeasure', 'Unit of measure'),
        flatField('amountOfProductInSku', 'Products per SKU'),
        flatField('grossWeight', 'Gross weight'),
        nestedField(
          'productDetails.productId',
          'Product ID',
          'productDetails',
          'productId'
        ),
      ],
      rowBlockers: skipWhenExcluded(inventorySkuBlockers),
    },
    Vehicle: {
      payloadOf: (row) => row?.vehicle ?? null,
      rowKeyOf: (sourceRow) => sourceRowKey(sourceRow),
      approveBodyKey: 'vehicleRows',
      fluentPrefix: 'VehicleRows',
      masterRoute: '/fleet-master',
      nouns: { singular: 'vehicle', plural: 'vehicles' },
      editableFields: [
        flatField('registrationNumber', 'Registration'),
        flatField('model', 'Model'),
        flatField('vehicleType', 'Type'),
        flatField('vehicleCategory', 'Category'),
        flatField('fuelType', 'Fuel type'),
        nestedField(
          'vehicleCapacity.maxPallets',
          'Max pallets',
          'vehicleCapacity',
          'maxPallets'
        ),
      ],
      rowBlockers: skipWhenExcluded(vehicleBlockers),
    },
  };

export const mergeSuggestionsWithOriginal = (
  importDoc: ApiMasterDataImport,
  useAnalysis: boolean
): ReviewRow[] => {
  const config = entityImportConfig[importDoc.entityType];
  const templateRows = importDoc.templateRows ?? [];
  const suggestedRows = useAnalysis ? importDoc.analysis?.rows ?? [] : [];

  const rowsByKey = new Map<string, ReviewRow>();
  const orderedRows: ReviewRow[] = [];
  const templatesBySourceRow = new Map<number, ReviewRow[]>();
  const matchedKeys = new Set<string>();

  const templateOrdinal = new Map<number, number>();
  templateRows.forEach((templateRow) => {
    const original = config.payloadOf(templateRow);
    const ordinal = templateOrdinal.get(templateRow.sourceRow) ?? 0;
    templateOrdinal.set(templateRow.sourceRow, ordinal + 1);
    const key = config.rowKeyOf(templateRow.sourceRow, original, ordinal);
    const reviewRow: ReviewRow = {
      key,
      sourceRow: templateRow.sourceRow,
      original,
      suggested: null,
      data: original ? { ...original } : {},
      issues: [],
      parseErrors: templateRow.parseErrors ?? [],
      excluded: !original,
    };
    rowsByKey.set(key, reviewRow);
    orderedRows.push(reviewRow);
    const siblings = templatesBySourceRow.get(templateRow.sourceRow) ?? [];
    siblings.push(reviewRow);
    templatesBySourceRow.set(templateRow.sourceRow, siblings);
  });

  const suggestionOrdinal = new Map<number, number>();
  suggestedRows.forEach((suggestedRow) => {
    const suggested = config.payloadOf(suggestedRow);
    const ordinal = suggestionOrdinal.get(suggestedRow.sourceRow) ?? 0;
    suggestionOrdinal.set(suggestedRow.sourceRow, ordinal + 1);
    const key = config.rowKeyOf(suggestedRow.sourceRow, suggested, ordinal);

    let target = rowsByKey.get(key);
    if (target && matchedKeys.has(target.key)) target = undefined;
    if (!target) {
      const siblings = templatesBySourceRow.get(suggestedRow.sourceRow) ?? [];
      target = siblings.find((row) => !matchedKeys.has(row.key));
    }

    if (target) {
      matchedKeys.add(target.key);
      target.suggested = suggested;
      target.issues = suggestedRow.issues ?? [];
      if (suggested) {
        target.data = { ...suggested };
        target.excluded = false;
      }
    } else {
      const newRow: ReviewRow = {
        key,
        sourceRow: suggestedRow.sourceRow,
        original: null,
        suggested,
        data: suggested ? { ...suggested } : {},
        issues: suggestedRow.issues ?? [],
        parseErrors: [],
        excluded: !suggested,
      };
      rowsByKey.set(key, newRow);
      orderedRows.push(newRow);
      matchedKeys.add(key);
    }
  });

  return orderedRows.sort((a, b) => a.sourceRow - b.sourceRow);
};

export const buildApproveRows = (
  rows: ReviewRow[],
  entityType: ImportEntityType = 'Customer'
): { sourceRow: number; data: ImportRowData }[] => {
  const fields = entityImportConfig[entityType].editableFields;
  return rows
    .filter((row) => !row.excluded && Object.keys(row.data).length > 0)
    .map((row) => {
      let data: ImportRowData = { ...row.data };
      fields.forEach((field) => {
        const current = field.getValue(data);
        if (typeof current === 'string' && current.trim() === '') {
          data = field.setValue(data, emptyToNull(current) as null);
        }
      });
      return { sourceRow: row.sourceRow, data };
    });
};

export const changedFields = (
  original: ImportRowData | null,
  suggested: ImportRowData | null
): string[] => {
  if (!original || !suggested) return [];
  const keys = new Set([...Object.keys(original), ...Object.keys(suggested)]);
  return [...keys].filter(
    (key) => JSON.stringify(original[key]) !== JSON.stringify(suggested[key])
  );
};

export const displayValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const rowBlockers = (
  row: ReviewRow,
  entityType: ImportEntityType
): RowBlocker[] => entityImportConfig[entityType].rowBlockers(row);

export const rowsNeedingAttention = (
  rows: ReviewRow[],
  entityType: ImportEntityType
): Set<number> => {
  const needingAttention = new Set<number>();
  rows.forEach((row) => {
    if (entityImportConfig[entityType].rowBlockers(row).length > 0) {
      needingAttention.add(row.sourceRow);
    }
  });
  return needingAttention;
};

export const buildRowErrorLookup = (
  rows: ReviewRow[],
  entityType: ImportEntityType,
  approveRowErrors: ApproveRowError[]
): Map<string, RowErrorState> => {
  const editableKeys = new Set(
    entityImportConfig[entityType].editableFields.map((field) => field.key)
  );
  const byKey = new Map<string, RowErrorState>();

  const entryFor = (key: string): RowErrorState => {
    const existing = byKey.get(key);
    if (existing) return existing;
    const created: RowErrorState = {
      fieldMessages: new Map<string, string[]>(),
      rowMessages: [],
    };
    byKey.set(key, created);
    return created;
  };

  const addMessage = (key: string, field: string | null, message: string) => {
    const entry = entryFor(key);
    if (field && editableKeys.has(field)) {
      const messages = entry.fieldMessages.get(field) ?? [];
      if (!messages.includes(message)) messages.push(message);
      entry.fieldMessages.set(field, messages);
    } else if (!entry.rowMessages.includes(message)) {
      entry.rowMessages.push(message);
    }
  };

  rows.forEach((row) => {
    entityImportConfig[entityType]
      .rowBlockers(row)
      .forEach((blocker) =>
        addMessage(row.key, blocker.field, blocker.message)
      );
  });

  approveRowErrors.forEach((error) => {
    rows
      .filter((row) => row.sourceRow === error.sourceRow)
      .forEach((row) => addMessage(row.key, error.field, error.message));
  });

  return byKey;
};

const parseFluentProperty = (
  propertyName: string,
  entityType: ImportEntityType
): { index: number; field: string | null } | null => {
  const prefix = entityImportConfig[entityType].fluentPrefix;
  if (!propertyName.startsWith(prefix)) return null;

  const indexMatch = propertyName.match(/\[(\d+)\]/);
  if (!indexMatch) return null;
  const index = Number(indexMatch[1]);

  const segments = propertyName.split('.');
  const dataIndex = segments.indexOf('Data');
  const isTopLevelDataField =
    dataIndex !== -1 &&
    segments.length === dataIndex + 2 &&
    !segments[dataIndex + 1].includes('[');
  const field = isTopLevelDataField
    ? pascalToCamel(segments[dataIndex + 1])
    : null;

  return { index, field };
};

export const mapApproveErrors = (
  rawErrors: unknown,
  submittedRows: { sourceRow: number; data: ImportRowData }[],
  entityType: ImportEntityType
): MappedApproveErrors => {
  const rowErrors: ApproveRowError[] = [];
  const generalErrors: string[] = [];
  const list = Array.isArray(rawErrors) ? rawErrors : [];

  list.forEach((rawError) => {
    if (typeof rawError === 'string') {
      generalErrors.push(rawError);
      return;
    }
    if (!rawError || typeof rawError !== 'object') return;

    const asFluent = rawError as {
      propertyName?: unknown;
      errorMessage?: unknown;
    };
    if (
      typeof asFluent.propertyName === 'string' &&
      typeof asFluent.errorMessage === 'string'
    ) {
      const parsed = parseFluentProperty(asFluent.propertyName, entityType);
      const submitted =
        parsed && parsed.index >= 0 ? submittedRows[parsed.index] : undefined;
      if (parsed && submitted) {
        rowErrors.push({
          sourceRow: submitted.sourceRow,
          field: parsed.field,
          message: asFluent.errorMessage,
        });
      } else {
        generalErrors.push(asFluent.errorMessage);
      }
      return;
    }

    const asCommitter = rawError as { message?: unknown; row?: unknown };
    if (typeof asCommitter.message === 'string') {
      const sourceRow =
        typeof asCommitter.row === 'number' ? asCommitter.row : 0;
      if (sourceRow > 0) {
        rowErrors.push({
          sourceRow,
          field: null,
          message: asCommitter.message,
        });
      } else {
        generalErrors.push(asCommitter.message);
      }
    }
  });

  return { rowErrors, generalErrors };
};
